import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  markOwnerNotificationRead,
  markOwnerNotificationsShown,
  selectOwnerNotifications,
  type OwnerNotification,
} from '@/store/slices/ownerNotifications';

import { Text } from '../../../../components/Text/Text';
import { Toast } from '../../../../components/Toast';
import { useToast } from '../../../../hooks/useToast';
import { ANIM } from '../../../../theme/tokens';
import { dateLocale } from '../../../../utils/locale';
import { followNotificationLink } from '../../notificationLink';
import { BellGlyph } from '../BellGlyph';
import {
  Announce,
  Count,
  Empty,
  Head,
  HeadTitle,
  Item,
  ItemBody,
  ItemDate,
  ItemLink,
  ItemMain,
  ItemTitle,
  List,
  MarkAll,
  Panel,
  Ringer,
  Trigger,
  Wrap,
} from './NotificationBell.styles';

/**
 * The top bar's notifications bell: how many are unread, and the list of what Prisma (the partner's team) sent
 * the owner, newest first. Clicking one marks it read; its link, when it has one, opens its page. The list comes
 * from the ownerNotifications slice, which the layout keeps fresh (useOwnerNotificationsSync) and the websocket
 * extends.
 *
 * A new one is announced here rather than in a pop-up, which would stop the owner at work: the bell rings, then its
 * red dot pops in. It rings only while the owner can see the tab; a hidden tab waits for them to come back. Once the
 * ring has played it counts as shown (Prisma sees it was seen); the owner opens it from the bell.
 */
export const NotificationBell = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { notifications, unread, loaded, failed, popup } = useAppSelector(selectOwnerNotifications);
  // A support agent signed in as the owner sees the bell, but reading is the owner's: Owner Hub ignores it.
  const impersonating = Boolean(useAppSelector((state) => state.authentication.user?.admin));
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const { toast, show } = useToast();

  // The newest one not announced yet (Owner Hub's `popup`): rings the bell once the owner can see the tab, then
  // counts as shown. A layout effect, so the dot never paints a frame before its pop; the cleanup stops a ring whose
  // notification went away (read, or announced elsewhere) mid-way.
  const announceId = impersonating ? undefined : popup?.id;
  const [ringing, setRinging] = React.useState<string | null>(null);
  React.useLayoutEffect(() => {
    if (!announceId) return undefined;
    let done = 0;
    const ring = (): void => {
      if (done || document.visibilityState !== 'visible') return;
      setRinging(announceId);
      done = window.setTimeout(() => {
        setRinging(null);
        void dispatch(markOwnerNotificationsShown(announceId));
      }, ANIM.BELL_RING_MS + ANIM.BELL_DOT_POP_MS);
    };
    ring();
    document.addEventListener('visibilitychange', ring);
    return () => {
      document.removeEventListener('visibilitychange', ring);
      window.clearTimeout(done);
      setRinging(null);
    };
  }, [announceId, dispatch]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const when = React.useMemo(() => new Intl.DateTimeFormat(dateLocale(i18n.language), { dateStyle: 'medium', timeStyle: 'short' }), [i18n.language]);

  // A read Owner Hub refused says so; the list is read again after it either way (the slice).
  const markRead = (id: string | undefined): void => {
    dispatch(markOwnerNotificationRead(id))
      .unwrap()
      .catch(() => show(t('Could not mark as read')));
  };
  const read = (n: OwnerNotification): void => {
    if (!n.read_at && !impersonating) markRead(n.id);
  };
  const date = (at: string | null): string | null => {
    const d = at ? new Date(at) : null;
    return d && !Number.isNaN(d.getTime()) ? when.format(d) : null;
  };
  const follow = (n: OwnerNotification): void => {
    read(n);
    setOpen(false);
    followNotificationLink(n.link_url, navigate);
  };

  return (
    <Wrap ref={ref}>
      <Trigger
        type="button"
        aria-label={t('Notifications')}
        title={t('Notifications')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((x) => !x)}
      >
        {/* Keyed on the one announced, so each new one plays it again; the bell's and the dot's keys differ, as
            siblings sharing a key leave an extra bell behind. */}
        <Ringer key={`bell-${ringing ?? ''}`} $ringing={Boolean(ringing)}>
          <BellGlyph />
        </Ringer>
        {unread > 0 ? <Count key={`count-${ringing ?? ''}`} $popping={Boolean(ringing)} text={unread > 99 ? '99+' : String(unread)} raw /> : null}
      </Trigger>
      {/* Always there and empty at rest, outside the button: a screen reader reads what arrives in a status region. */}
      <Announce role="status">{ringing ? <Text text="New notification" /> : null}</Announce>
      {open ? (
        <Panel role="dialog" aria-label={t('Notifications')}>
          <Head>
            <HeadTitle text="Notifications" />
            {unread > 0 && !impersonating ? (
              <MarkAll type="button" onClick={() => markRead(undefined)}>
                <Text text="Mark all read" />
              </MarkAll>
            ) : null}
          </Head>
          <List>
            {notifications.length === 0 && !loaded ? <Empty text="Loading…" /> : null}
            {notifications.length === 0 && loaded && failed ? <Empty text="Notifications could not be loaded" /> : null}
            {notifications.length === 0 && loaded && !failed ? <Empty text="No notifications yet" /> : null}
            {notifications.length === 0 ? null : (
              notifications.map((n) => (
                <Item key={n.id} $unread={!n.read_at}>
                  <ItemMain type="button" disabled={Boolean(n.read_at) || impersonating} onClick={() => read(n)}>
                    {/* Written by the partner's team: shown as sent, never translated. */}
                    <ItemTitle text={n.title} raw />
                    {n.body ? <ItemBody text={n.body} raw /> : null}
                    {date(n.created_at) ? <ItemDate>{date(n.created_at)}</ItemDate> : null}
                  </ItemMain>
                  {n.link_url ? (
                    <ItemLink type="button" onClick={() => follow(n)}>
                      {n.link_label ? <Text text={n.link_label} raw /> : <Text text="Open" />}
                    </ItemLink>
                  ) : null}
                </Item>
              ))
            )}
          </List>
        </Panel>
      ) : null}
      <Toast toast={toast} />
    </Wrap>
  );
};
